import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const ESTADO_STYLE = {
  nueva: "bg-cream/15 text-cream",
  pendiente: "bg-amber-400/20 text-amber-200",
  confirmada: "bg-olive/40 text-olivelight",
  rechazada: "bg-red-500/20 text-red-200",
  cancelada: "bg-white/10 text-cream/50",
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      sessionStorage.setItem("cs_admin_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      toast.error(formatApiError(err, "Credenciales incorrectas."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-charcoal px-5 text-cream">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-ember p-9">
        <div className="flex items-center gap-3">
          <Lock size={18} className="text-terra" />
          <h1 className="font-serif text-2xl">Wild Spoon · Panel</h1>
        </div>
        <p className="mt-2 text-xs text-cream/50">Gestión de solicitudes y mensajes.</p>
        <div className="mt-8 space-y-5">
          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-cream/60" htmlFor="a-email">Email</label>
            <input
              id="a-email"
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-dark"
            />
          </div>
          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-cream/60" htmlFor="a-pass">Contraseña</label>
            <input
              id="a-pass"
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-dark"
            />
          </div>
        </div>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-8 w-full rounded-full bg-terra py-3.5 text-sm font-medium text-cream transition-colors hover:bg-terra-dark disabled:opacity-60"
        >
          {loading ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const setEstado = async (id, estado) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { estado });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      toast.success(
        estado === "confirmada" ? "Reserva confirmada — email enviado al cliente" :
        estado === "rechazada" ? "Solicitud rechazada — email enviado al cliente" :
        `Solicitud marcada como ${estado}`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Solicitud eliminada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!bookings) return <p className="text-cream/50">Cargando…</p>;
  if (!bookings.length) return <p className="text-cream/50">Todavía no hay solicitudes.</p>;

  return (
    <div className="space-y-4" data-testid="admin-bookings-list">
      {bookings.map((b) => (
        <div key={b.id} data-testid="admin-booking-card" className="border border-white/10 bg-ember p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-3 font-serif text-xl text-cream">
                {b.nombre} {b.apellidos}
                <span data-testid={`admin-booking-badge-${b.id}`} className={`rounded-full px-3 py-0.5 text-[10px] uppercase tracking-widest ${ESTADO_STYLE[b.estado] || "bg-white/10 text-cream"}`}>
                  {b.estado}
                </span>
              </p>
              <p className="mt-1 text-sm text-cream/60">
                {b.email} · {b.telefono}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {b.estado !== "confirmada" && (
                <button
                  data-testid={`admin-booking-confirm-${b.id}`}
                  onClick={() => setEstado(b.id, "confirmada")}
                  className="rounded-full bg-olive px-4 py-1.5 text-xs font-medium text-cream transition-all hover:opacity-90"
                >
                  Confirmar reserva
                </button>
              )}
              {b.estado !== "pendiente" && b.estado !== "confirmada" && (
                <button
                  data-testid={`admin-booking-pending-${b.id}`}
                  onClick={() => setEstado(b.id, "pendiente")}
                  className="rounded-full border border-amber-300/40 px-4 py-1.5 text-xs text-amber-200 transition-colors hover:bg-amber-300/10"
                >
                  En espera
                </button>
              )}
              {b.estado !== "rechazada" && b.estado !== "confirmada" && b.estado !== "cancelada" && (
                <button
                  data-testid={`admin-booking-reject-${b.id}`}
                  onClick={() => setEstado(b.id, "rechazada")}
                  className="rounded-full border border-red-300/40 px-4 py-1.5 text-xs text-red-200 transition-colors hover:bg-red-300/10"
                >
                  Rechazar
                </button>
              )}
              {b.estado !== "cancelada" && (
                <button
                  data-testid={`admin-booking-cancel-${b.id}`}
                  onClick={() => setEstado(b.id, "cancelada")}
                  className="rounded-full border border-white/20 px-4 py-1.5 text-xs text-cream/60 transition-colors hover:text-cream"
                >
                  Cancelar
                </button>
              )}
              <button
                data-testid={`admin-booking-delete-${b.id}`}
                onClick={() => remove(b.id)}
                className="p-2 text-cream/40 transition-colors hover:text-red-300"
                aria-label="Eliminar"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">Solicitada</p>
              <p className="text-cream/80">{(b.created_at || "").slice(0, 10)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">Fecha deseada</p>
              <p className="text-cream/80">{b.fecha_deseada}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">Personas</p>
              <p className="text-cream/80">{b.personas}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">Tipo</p>
              <p className="text-cream/80">
                {b.tipo_experiencia === "edouard" ? "En casa de Edu — Albaicín" : "Chef a domicilio"}
              </p>
            </div>
          </div>
          {(b.direccion || b.idioma || b.alergias || b.mensaje) && (
            <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4 text-sm text-cream/60">
              {b.direccion && <p><span className="text-cream/40">Dirección:</span> {b.direccion}</p>}
              {b.idioma && <p><span className="text-cream/40">Idioma:</span> {b.idioma}</p>}
              {b.alergias && <p><span className="text-cream/40">Alergias:</span> {b.alergias}</p>}
              {b.mensaje && <p><span className="text-cream/40">Mensaje:</span> {b.mensaje}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState(null);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((ms) => ms.filter((m) => m.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!messages) return <p className="text-cream/50">Cargando…</p>;
  if (!messages.length) return <p className="text-cream/50">Todavía no hay mensajes.</p>;

  return (
    <div className="space-y-4" data-testid="admin-messages-list">
      {messages.map((m) => (
        <div key={m.id} data-testid="admin-message-card" className="border border-white/10 bg-ember p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-serif text-lg text-cream">{m.nombre}</p>
              <p className="text-sm text-cream/60">
                {m.email} {m.telefono ? `· ${m.telefono}` : ""}
              </p>
            </div>
            <button onClick={() => remove(m.id)} className="p-2 text-cream/40 hover:text-red-300" aria-label="Eliminar">
              <Trash2 size={16} />
            </button>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-cream/75">{m.mensaje}</p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-cream/40">
            {(m.created_at || "").slice(0, 10)}
          </p>
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) =>
      setForm({
        telefono: r.data.telefono || "",
        whatsapp: r.data.whatsapp || "",
        email: r.data.email || "",
        direccion: r.data.direccion || "",
        notification_email: "",
      })
    );
  }, []);

  if (!form) return <p className="text-cream/50">Cargando…</p>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", form);
      toast.success("Ajustes guardados");
      setForm((f) => ({ ...f, notification_email: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fields = [
    ["telefono", "Teléfono público"],
    ["whatsapp", "WhatsApp (con prefijo país, p. ej. 34600000000)"],
    ["email", "Email de contacto público"],
    ["direccion", "Zona de servicio"],
    ["notification_email", "Email donde recibir el aviso de nuevas solicitudes"],
  ];

  return (
    <form onSubmit={submit} data-testid="admin-settings-form" className="max-w-xl space-y-5">
      {fields.map(([key, label]) => (
        <div key={key}>
          <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-cream/60" htmlFor={`s-${key}`}>
            {label}
          </label>
          <input
            id={`s-${key}`}
            data-testid={`admin-settings-${key}`}
            className="field-dark"
            value={form[key]}
            onChange={(e) => set(key, e.target.value)}
          />
        </div>
      ))}
      <button
        data-testid="admin-settings-submit"
        type="submit"
        className="rounded-full bg-terra px-8 py-3 text-sm font-medium text-cream transition-colors hover:bg-terra-dark"
      >
        Guardar ajustes
      </button>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("cs_admin_token"));
  const [tab, setTab] = useState("solicitudes");

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => meta.setAttribute("content", "index, follow");
  }, []);

  if (!token) {
    return (
      <Login
        onLogin={() => setToken(sessionStorage.getItem("cs_admin_token"))}
      />
    );
  }

  const logout = () => {
    sessionStorage.removeItem("cs_admin_token");
    setToken(null);
  };

  const tabs = [
    ["solicitudes", "Solicitudes"],
    ["mensajes", "Mensajes"],
    ["ajustes", "Ajustes"],
  ];

  return (
    <div className="min-h-screen bg-charcoal px-5 py-10 text-cream md:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <h1 className="font-serif text-2xl">Wild Spoon · Panel</h1>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-xs text-cream/70 transition-colors hover:border-cream hover:text-cream"
          >
            <LogOut size={14} />
            Salir
          </button>
        </div>
        <div className="flex gap-6 py-6">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`pb-1 text-sm transition-colors ${
                tab === key ? "border-b border-terra text-terra" : "text-cream/50 hover:text-cream"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "solicitudes" && <BookingsTab />}
        {tab === "mensajes" && <MessagesTab />}
        {tab === "ajustes" && <SettingsTab />}
      </div>
    </div>
  );
}
